import React, { useState, useEffect, useRef } from "react";
import { request } from "app/network";
import logoImg from "assets/images/logo-ct.png";

// ── 브라우저 탭 타이틀 & 파비콘 교체 유틸 ──────────────
const setFavicon = (href: string) => {
    let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
    if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
    }
    link.href = href;
};

// ── 문의 유형 옵션 ─────────────────────────────────────
const CATEGORY_OPTIONS = [
    { value: "INQUIRY_ACCOUNT_DELETE", label: "계정 삭제 요청" },
    { value: "INQUIRY_POINT", label: "포인트 문의" },
    { value: "INQUIRY_APP", label: "앱 오류/버그" },
    { value: "INQUIRY_ETC", label: "기타" },
];

// ── 스타일 상수 ───────────────────────────────────────
const colors = {
    primary: "#2563EB",
    primaryLight: "#EFF6FF",
    primaryHover: "#1D4ED8",
    success: "#16A34A",
    successLight: "#F0FDF4",
    danger: "#DC2626",
    dangerLight: "#FEF2F2",
    dangerBorder: "#FECACA",
    textPrimary: "#0F172A",
    textSecondary: "#475569",
    textMuted: "#94A3B8",
    bg: "#F8FAFC",
    card: "#FFFFFF",
    border: "#E2E8F0",
    inputBorder: "#CBD5E1",
};

const inputStyle = (focused: boolean): React.CSSProperties => ({
    width: "100%",
    padding: "11px 14px",
    border: `1.5px solid ${focused ? colors.primary : colors.inputBorder}`,
    borderRadius: 10,
    fontFamily: "'Inter', sans-serif",
    fontSize: 14,
    color: colors.textPrimary,
    background: "#fff",
    outline: "none",
    boxShadow: focused ? "0 0 0 3px rgba(37,99,235,.12)" : "none",
    transition: "border-color .2s, box-shadow .2s",
    boxSizing: "border-box",
});

// ── 폼 상태 타입 ──────────────────────────────────────
interface FormState {
    categoryCode: string;
    name: string;
    phone: string;
    email: string;
    title: string;
    content: string;
}

interface FieldErrors {
    categoryCode?: string;
    name?: string;
    contact?: string;
    title?: string;
}

// ── 메인 컴포넌트 ─────────────────────────────────────
const PublicInquiryScreen: React.FC = () => {
    const [form, setForm] = useState<FormState>({
        categoryCode: "",
        name: "",
        phone: "",
        email: "",
        title: "",
        content: "",
    });

    const [errors, setErrors] = useState<FieldErrors>({});
    const [focused, setFocused] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState<{ type: "success" | "error"; msg: string } | null>(null);
    const [submitted, setSubmitted] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // 드롭다운 외부 클릭 시 닫기
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                setDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    // ── 탭 타이틀 & 파비콘 교체 ─────────────────────────
    useEffect(() => {
        const prevTitle = document.title;
        const prevFavicon = document.querySelector<HTMLLinkElement>("link[rel~='icon']")?.href ?? "";

        document.title = "문의하기 | 메디인스텝";
        setFavicon(`${window.location.origin}/favicon-inquiry.png`);

        return () => {
            document.title = prevTitle;
            setFavicon(prevFavicon);
        };
    }, []);


    const handleChange = (field: keyof FormState) =>
        (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
            setForm(prev => ({ ...prev, [field]: e.target.value }));
            setErrors(prev => ({ ...prev, [field]: undefined, contact: undefined }));
        };

    // ── 유효성 검사 ─────────────────────────────────────
    const validate = (): boolean => {
        const errs: FieldErrors = {};
        if (!form.categoryCode) errs.categoryCode = "문의 유형을 선택해 주세요.";
        if (!form.name.trim()) errs.name = "이름을 입력해 주세요.";
        if (!form.phone.trim() && !form.email.trim())
            errs.contact = "전화번호 또는 이메일 중 하나를 입력해 주세요.";
        if (!form.title.trim()) errs.title = "제목을 입력해 주세요.";
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    // ── 제출 ─────────────────────────────────────────────
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setResult(null);
        if (!validate()) return;

        setLoading(true);
        try {
            const res = await request("Inquiry.createPublic", {
                paramz: {
                    tenantSeqId: 1,
                    categoryCode: form.categoryCode,
                    title: form.title.trim(),
                    content: form.content.trim() || null,
                    name: form.name.trim(),
                    phone: form.phone.trim() || null,
                    email: form.email.trim() || null,
                },
            });

            if (res && res.data) {
                setSubmitted(true);
            } else {
                setResult({ type: "error", msg: "제출에 실패했습니다. 다시 시도해 주세요." });
            }
        } catch (err: any) {
            setResult({
                type: "error",
                msg: err?.response?.data?.message ?? "서버 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.",
            });
        } finally {
            setLoading(false);
        }
    };

    // ── 완료 화면 ────────────────────────────────────────
    if (submitted) {
        return (
            <div style={{
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                minHeight: "100vh",
                background: colors.bg,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "24px 16px 48px",
            }}>
                <div style={{
                    background: colors.card,
                    borderRadius: 16,
                    boxShadow: "0 4px 24px rgba(15,23,42,.08)",
                    maxWidth: 540,
                    width: "100%",
                    padding: "56px 36px",
                    textAlign: "center",
                }}>
                    {/* 성공 아이콘 */}
                    <div style={{
                        width: 72, height: 72,
                        borderRadius: "50%",
                        background: colors.successLight,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        margin: "0 auto 24px",
                    }}>
                        <svg width={36} height={36} viewBox="0 0 24 24" fill="none"
                            stroke={colors.success} strokeWidth={2.5}
                            strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12" />
                        </svg>
                    </div>

                    <h2 style={{ fontSize: 22, fontWeight: 700, color: colors.textPrimary, marginBottom: 12 }}>
                        문의가 접수되었습니다
                    </h2>
                    <p style={{ fontSize: 14, color: colors.textSecondary, lineHeight: 1.7, marginBottom: 32 }}>
                        담당자 확인 후 <strong>영업일 기준 3~5일 이내</strong>에<br />
                        입력하신 연락처로 답변 드리겠습니다.
                    </p>

                    {/* 처리 기간 안내 */}
                    <div style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 8,
                        background: colors.primaryLight,
                        borderRadius: 10,
                        padding: "10px 18px",
                        marginBottom: 36,
                    }}>
                        <svg width={14} height={14} viewBox="0 0 24 24" fill="none"
                            stroke={colors.primary} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" />
                        </svg>
                        <span style={{ fontSize: 13, color: colors.primary, fontWeight: 500 }}>
                            평균 답변 기간: 영업일 기준 <strong>3~5일</strong>
                        </span>
                    </div>

                    <div style={{ height: 1, background: colors.border, marginBottom: 24 }} />
                    <p style={{ fontSize: 12, color: colors.textMuted }}>
                        © 2025 MediInStep. All rights reserved.
                    </p>
                </div>
                <style>{`
                    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
                `}</style>
            </div>
        );
    }

    // ── 렌더 ─────────────────────────────────────────────
    return (
        <div style={{
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
            minHeight: "100vh",
            background: colors.bg,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "24px 16px 48px",
        }}>
            <div style={{
                background: colors.card,
                borderRadius: 16,
                boxShadow: "0 4px 24px rgba(15,23,42,.08)",
                maxWidth: 540,
                width: "100%",
                padding: "40px 36px",
            }}>

                {/* 로고 */}
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 28 }}>
                    <img
                        src={logoImg}
                        alt="MediInStep"
                        style={{ width: 56, height: 56, objectFit: "contain", flexShrink: 0 }}
                    />
                    <span style={{ fontSize: 22, fontWeight: 800, color: colors.primary, letterSpacing: "-.4px" }}>
                        메디인스텝
                    </span>
                </div>

                {/* 제목 */}
                <h1 style={{
                    fontSize: 26, fontWeight: 700,
                    color: colors.textPrimary, letterSpacing: "-.5px",
                    marginBottom: 6,
                }}>
                    문의하기
                </h1>
                <p style={{ fontSize: 14, color: colors.textSecondary, marginBottom: 28 }}>
                    문의 내용을 작성해 주시면 담당자가 확인 후 빠르게 답변 드리겠습니다.
                </p>

                {/* 폼 */}
                <form onSubmit={handleSubmit} noValidate>

                    {/* 문의 유형 – 커스텀 드롭다운 */}
                    <FormGroup label="문의 유형" required error={errors.categoryCode}>
                        <div ref={dropdownRef} style={{ position: "relative" }}>
                            {/* 트리거 버튼 */}
                            <button
                                type="button"
                                onClick={() => {
                                    setDropdownOpen(prev => !prev);
                                    setErrors(prev => ({ ...prev, categoryCode: undefined }));
                                }}
                                style={{
                                    ...inputStyle(dropdownOpen),
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    cursor: "pointer",
                                    background: "#fff",
                                    textAlign: "left",
                                    color: form.categoryCode ? colors.textPrimary : colors.textMuted,
                                }}
                            >
                                <span>
                                    {CATEGORY_OPTIONS.find(o => o.value === form.categoryCode)?.label
                                        ?? "문의 유형을 선택해 주세요"}
                                </span>
                                <svg width={12} height={12} viewBox="0 0 24 24" fill="none"
                                    stroke={colors.textMuted} strokeWidth={2}
                                    style={{
                                        flexShrink: 0, transition: "transform .2s",
                                        transform: dropdownOpen ? "rotate(180deg)" : "none"
                                    }}>
                                    <polyline points="6 9 12 15 18 9" />
                                </svg>
                            </button>

                            {/* 옵션 목록 */}
                            {dropdownOpen && (
                                <ul style={{
                                    position: "absolute",
                                    top: "calc(100% + 4px)",
                                    left: 0,
                                    right: 0,
                                    background: "#fff",
                                    border: `1px solid ${colors.border}`,
                                    borderRadius: 10,
                                    boxShadow: "0 8px 24px rgba(15,23,42,.12)",
                                    zIndex: 1000,
                                    margin: 0,
                                    padding: "4px 0",
                                    listStyle: "none",
                                    overflow: "hidden",
                                }}>
                                    {CATEGORY_OPTIONS.map(opt => (
                                        <li
                                            key={opt.value}
                                            onClick={() => {
                                                setForm(prev => ({ ...prev, categoryCode: opt.value }));
                                                setDropdownOpen(false);
                                            }}
                                            style={{
                                                padding: "11px 14px",
                                                fontSize: 14,
                                                cursor: "pointer",
                                                color: form.categoryCode === opt.value
                                                    ? colors.primary : colors.textPrimary,
                                                background: form.categoryCode === opt.value
                                                    ? colors.primaryLight : "transparent",
                                                fontWeight: form.categoryCode === opt.value ? 600 : 400,
                                                transition: "background .15s",
                                            }}
                                            onMouseEnter={e =>
                                                (e.currentTarget.style.background = colors.primaryLight)}
                                            onMouseLeave={e =>
                                            (e.currentTarget.style.background =
                                                form.categoryCode === opt.value
                                                    ? colors.primaryLight : "transparent")}
                                        >
                                            {opt.label}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </FormGroup>

                    {/* 계정 삭제 경고 – 삭제 요청 선택 시만 표시 */}
                    {form.categoryCode === "INQUIRY_ACCOUNT_DELETE" && (
                        <div style={{
                            background: colors.dangerLight,
                            border: `1.5px solid ${colors.dangerBorder}`,
                            borderRadius: 10,
                            padding: "13px 15px",
                            marginBottom: 8,
                            display: "flex",
                            gap: 10,
                            alignItems: "flex-start",
                        }}>
                            <span style={{ fontSize: 17, flexShrink: 0, marginTop: 1 }}>⚠️</span>
                            <div>
                                <p style={{ fontSize: 13, fontWeight: 700, color: colors.danger, marginBottom: 4 }}>
                                    삭제 전 반드시 확인하세요
                                </p>
                                <p style={{ fontSize: 13, color: "#7F1D1D", lineHeight: 1.65 }}>
                                    계정을 삭제하면 <strong>운동 기록, 포인트, 개인정보</strong> 등 모든 데이터가{" "}
                                    <strong>영구 삭제</strong>되며 복구할 수 없습니다.
                                </p>
                            </div>
                        </div>
                    )}

                    {/* 이름 */}
                    <FormGroup label="이름" required error={errors.name}>
                        <input
                            type="text"
                            value={form.name}
                            onChange={handleChange("name")}
                            placeholder="홍길동"
                            maxLength={50}
                            style={inputStyle(focused === "name")}
                            onFocus={() => setFocused("name")}
                            onBlur={() => setFocused(null)}
                        />
                    </FormGroup>

                    {/* 전화번호 */}
                    <FormGroup label="전화번호" error={errors.contact}
                        hint="전화번호 또는 이메일 중 하나는 필수입니다.">
                        <input
                            type="tel"
                            value={form.phone}
                            onChange={handleChange("phone")}
                            placeholder="010-0000-0000"
                            maxLength={20}
                            style={inputStyle(focused === "phone")}
                            onFocus={() => setFocused("phone")}
                            onBlur={() => setFocused(null)}
                        />
                    </FormGroup>

                    {/* 이메일 */}
                    <FormGroup label="이메일" hint="답변을 이메일로 받으시려면 입력해 주세요.">
                        <input
                            type="email"
                            value={form.email}
                            onChange={handleChange("email")}
                            placeholder="example@email.com"
                            maxLength={100}
                            style={inputStyle(focused === "email")}
                            onFocus={() => setFocused("email")}
                            onBlur={() => setFocused(null)}
                        />
                    </FormGroup>

                    {/* 제목 */}
                    <FormGroup label="제목" required error={errors.title}>
                        <input
                            type="text"
                            value={form.title}
                            onChange={handleChange("title")}
                            placeholder="문의 제목을 입력해 주세요"
                            maxLength={200}
                            style={inputStyle(focused === "title")}
                            onFocus={() => setFocused("title")}
                            onBlur={() => setFocused(null)}
                        />
                    </FormGroup>

                    {/* 내용 */}
                    <FormGroup label="문의 내용" hint="(선택 사항)">
                        <textarea
                            value={form.content}
                            onChange={handleChange("content")}
                            placeholder="문의하실 내용을 자유롭게 적어 주세요."
                            style={{ ...inputStyle(focused === "content"), minHeight: 100, resize: "vertical" }}
                            onFocus={() => setFocused("content")}
                            onBlur={() => setFocused(null)}
                        />
                    </FormGroup>

                    {/* 제출 버튼 */}
                    <button
                        type="submit"
                        disabled={loading}
                        style={{
                            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                            width: "100%", padding: "14px 20px", marginTop: 8,
                            background: "linear-gradient(135deg,#2563EB,#3B82F6)",
                            color: "#fff", border: "none", borderRadius: 10,
                            fontFamily: "'Inter',sans-serif", fontSize: 15, fontWeight: 600,
                            cursor: loading ? "not-allowed" : "pointer",
                            boxShadow: "0 4px 14px rgba(37,99,235,.35)",
                            opacity: loading ? 0.7 : 1,
                            transition: "opacity .2s",
                        }}
                    >
                        {loading ? (
                            <>
                                <span style={{
                                    width: 18, height: 18,
                                    border: "2.5px solid rgba(255,255,255,.4)",
                                    borderTopColor: "#fff", borderRadius: "50%",
                                    display: "inline-block",
                                    animation: "spin .7s linear infinite",
                                }} />
                                제출 중...
                            </>
                        ) : (
                            <>
                                <svg width={17} height={17} viewBox="0 0 24 24" fill="none"
                                    stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="22" y1="2" x2="11" y2="13" />
                                    <polygon points="22 2 15 22 11 13 2 9 22 2" />
                                </svg>
                                문의 접수하기
                            </>
                        )}
                    </button>

                    {/* 결과 메시지 */}
                    {result && (
                        <div style={{
                            marginTop: 16, padding: 16, borderRadius: 10,
                            background: result.type === "success" ? colors.successLight : colors.dangerLight,
                            border: `1.5px solid ${result.type === "success" ? "#BBF7D0" : colors.dangerBorder}`,
                            color: result.type === "success" ? colors.success : colors.danger,
                            fontSize: 14, fontWeight: 500,
                        }}>
                            {result.type === "success" ? "✅ " : "⚠️ "}
                            {result.msg}
                        </div>
                    )}
                </form>

                {/* 처리 기간 */}
                <div style={{
                    display: "flex", alignItems: "center", gap: 8,
                    background: colors.primaryLight, borderRadius: 10,
                    padding: "11px 14px", marginTop: 20,
                }}>
                    <svg width={15} height={15} viewBox="0 0 24 24" fill="none"
                        stroke={colors.primary} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" />
                    </svg>
                    <p style={{ fontSize: 13, color: colors.primary, fontWeight: 500 }}>
                        평균 답변 기간: 영업일 기준 <strong>3~5일</strong> 소요됩니다.
                    </p>
                </div>

                {/* 구분선 / 카피라이트 */}
                <div style={{ height: 1, background: colors.border, margin: "20px 0" }} />
                <p style={{ textAlign: "center", fontSize: 12, color: colors.textMuted }}>
                    © 2025 MediInStep. All rights reserved.
                </p>
            </div>

            <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
      `}</style>
        </div>
    );
};

// ── 폼 그룹 래퍼 ─────────────────────────────────────
const FormGroup: React.FC<{
    label: string;
    required?: boolean;
    error?: string;
    hint?: string;
    children: React.ReactNode;
}> = ({ label, required, error, hint, children }) => (
    <div style={{ marginBottom: 16 }}>
        <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#0F172A", marginBottom: 6 }}>
            {label}{required && <span style={{ color: "#DC2626", marginLeft: 2 }}>*</span>}
        </label>
        {children}
        {hint && !error && <p style={{ fontSize: 12, color: "#94A3B8", marginTop: 5 }}>{hint}</p>}
        {error && <p style={{ fontSize: 12, color: "#DC2626", marginTop: 5 }}>{error}</p>}
    </div>
);

export default PublicInquiryScreen;
